import React, { useEffect, useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getPaginationRowModel,
  getFilteredRowModel,
  getSortedRowModel,
} from "@tanstack/react-table";
import * as XLSX from "xlsx";

function Table(props) {
  const [filtering, setFiltering] = useState("");
  const [rowSelection, setRowSelection] = useState({});
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [tableData, setTableData] = useState(props.data);
  const [deleteOption] = useState(props.deleteButton);
  const lang = props.lang;

  const data = useMemo(() => tableData, [tableData]);
  const columns = props.columns;

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      globalFilter: filtering,
      rowSelection: rowSelection,
      sorting: sorting,
      columnFilters: columnFilters,
    },
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    onSortingChange: setSorting,
    onGlobalFilterChange: setFiltering,
    onColumnFiltersChange: setColumnFilters,
  });

  const handleExportExcel = () => {
    const sheetData = tableData.map((row, rowIndex) =>
      columns.reduce((acc, col) => {
        let cellValue = "";

        // Get the base value (if accessorKey exists)
        const value = col.accessorKey ? row[col.accessorKey] : undefined;

        // If cell function exists, call it properly
        if (col.cell) {
          try {
            cellValue = col.cell({
              getValue: () => value,
              row: { original: row, index: rowIndex },
            });
          } catch (err) {
            console.error("Error in cell renderer:", err);
            cellValue = "";
          }
        } else if (col.accessorKey) {
          cellValue = value;
        }

        // Clean up values for Excel
        if (
          typeof cellValue !== "string" &&
          typeof cellValue !== "number" &&
          cellValue !== null &&
          cellValue !== undefined
        ) {
          cellValue = String(cellValue);
        }

        acc[col.header] = cellValue ?? "";
        return acc;
      }, {})
    );

    // Create workbook and sheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(sheetData);
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, "table-data.xlsx");
  };

  useEffect(() => {
    setTableData(props.data);
  }, [props.data]);

  return (
    <div>
      {/* Top search & delete */}
      <div className="card-body row gx-2 justify-content-end align-items-center">
        {props.exportOption && (
          <>
            <div className="col-6 col-lg-2">
              <button
                className="btn btn-primary w-100 mb-4"
                onClick={handleExportExcel}
              >
                {lang.Export_Text || "Excel"}
              </button>
            </div>
          </>
        )}
        <div className="col-6 col-lg-3">
          <input
            type="search"
            className="rounded mb-4 border p-1 w-100"
            placeholder={`${lang.Search || "Search"}`}
            value={filtering}
            onChange={(e) => setFiltering(e.target.value)}
          />
        </div>
        {deleteOption && (
          <div className="col-6 col-lg-2">
            <button
              className="btn btn-danger w-100 mb-4"
              onClick={(e) => {
                e.preventDefault();
                props.returnTable(table.getSelectedRowModel().flatRows);
              }}
            >
              {lang.Delete_Text || "Delete"}
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="">
        <table className="w-100 table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    style={{ verticalAlign: "top" }}
                  >
                    <div className="">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {
                        {
                          asc: " 🔼",
                          desc: " 🔽",
                        }[header.column.getIsSorted() ?? null]
                      }
                    </div>
                    {/* Column Filter Input */}
                    {props.colFilter?.includes(
                      header.column.columnDef.header
                    ) &&
                      (header.column.columnDef.meta?.filterOptions ? (
                        <select
                          value={header.column.getFilterValue() ?? ""}
                          onChange={(e) =>
                            header.column.setFilterValue(
                              e.target.value || undefined
                            )
                          }
                          className="form-select form-select-sm mt-1"
                        >
                          <option value="">All</option>
                          {header.column.columnDef.meta.filterOptions.map(
                            (opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            )
                          )}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={header.column.getFilterValue() ?? ""}
                          onChange={(e) =>
                            header.column.setFilterValue(e.target.value)
                          }
                          placeholder={`Filter ${header.column.columnDef.header}`}
                          className="form-control form-control-sm mt-1"
                        />
                      ))}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {props.footer && (
            <tfoot>
              {table.getFooterGroups().map((footerGroup) => (
                <tr key={footerGroup.id}>
                  {footerGroup.headers.map((header) => (
                    <th key={header.id}>
                      {flexRender(
                        header.column.columnDef.footer,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </tfoot>
          )}
        </table>
      </div>

      {/* Pagination controls */}
      <div className="d-flex flex-column flex-md-row mt-5 w-100 justify-content-between">
        <div>
          <span>
            <select
              className="btn"
              value={table.getState().pagination.pageSize}
              onChange={(e) => {
                table.setPageSize(Number(e.target.value));
              }}
            >
              {[10, 20, 30, 40, 50, 100].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </select>
          </span>
          <span className="ps-2">
            {lang.items_per_page || "items per page"}
          </span>
          <span>
            1-{table.getRowModel().rows.length} {lang.of || "of"} {data.length}{" "}
            {lang.Items || "items"}
          </span>
        </div>
        <div>
          <span>
            <input
              className="next-page text-center"
              style={{ width: "2.5rem" }}
              type="text"
              defaultValue={table.getState().pagination.pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                table.setPageIndex(page);
              }}
            />
          </span>
          <span className="px-2">
            {lang.of || "of"} {table.getPageCount()} {lang.pages || "pages"}
          </span>
          <span>
            <button
              className="btn-01 border"
              disabled={!table.getCanPreviousPage()}
              onClick={() => table.previousPage()}
            >
              ◀
            </button>
            <button
              className="btn-01 border"
              disabled={!table.getCanNextPage()}
              onClick={() => table.nextPage()}
            >
              ▶
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}

export default Table;
